import 'App.scss';

import {
  MarketplaceGeneralSettingsResponse,
  MarketplaceGeneralSettingsUpdateRequest,
} from 'APIs/marketplaces/marketplaces.general.settings.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { supportedCustomerTypes } from 'Constants/Constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';

import { yupResolver } from '@hookform/resolvers/yup';

import { Container, FormItem, FormWrapper, Label } from './styles';
import {
  GeneralSettingsUpdateFormProps,
  toGeneralSettingsUpdateFormProps,
  toMarketplaceGeneralSettingsUpdateRequest,
} from './types';
import { validationSchema } from './validation.schema';

type Props = {
  generalSettings: MarketplaceGeneralSettingsResponse | null;
  isLoading: boolean;
  errorMsg: string;
  updateGeneralSettings: (data: MarketplaceGeneralSettingsUpdateRequest) => Promise<void>;
  setShowUpdateModal: (showUpdateModal: boolean) => void;
};

const MarketplaceGeneralSettingsForm = ({
  generalSettings,
  isLoading,
  errorMsg,
  updateGeneralSettings,
  setShowUpdateModal,
}: Props) => {
  const [hideError, setHideError] = useState<boolean>(false);

  const errorContainerRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<GeneralSettingsUpdateFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: generalSettings ? toGeneralSettingsUpdateFormProps(generalSettings) : {},
  });

  const onSubmit = useCallback(
    async (data) => {
      const reqData = toMarketplaceGeneralSettingsUpdateRequest(generalSettings, data);
      reqData && updateGeneralSettings(reqData);
    },
    [generalSettings, updateGeneralSettings],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = useCallback(() => {
    setShowUpdateModal(false);
  }, [setShowUpdateModal]);

  useEffect(() => {
    if (errorMsg && errorContainerRef && errorContainerRef.current) {
      setHideError(false);
      errorContainerRef.current.scrollIntoView();
    }
  }, [errorMsg]);

  return (
    <Modal
      title="Edit Marketplace General Settings"
      cancelBtnText="Cancel"
      confirmBtnText="Save"
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isLoading}
      isLoading={isLoading}
      large={true}
      testId={'marketplace-general-settings-update-modal'}
    >
      <Container>
        <FormWrapper>
          <div ref={errorContainerRef}>
            {errorMsg && !hideError && (
              <Message
                title="Failed to save settings"
                success={false}
                description={errorMsg}
                handleClose={() => setHideError(true)}
              ></Message>
            )}
          </div>
          <FormItem>
            <Label>Supported customer types*</Label>
            <Controller
              name="supportedCustomerTypes"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  className={errors.supportedCustomerTypes ? 'react-select-container error' : 'react-select-container'}
                  placeholder="Select customer types"
                  options={supportedCustomerTypes}
                  onChange={(selectedOptions: any) => {
                    return field.onChange(selectedOptions);
                  }}
                />
              )}
            />
            {errors.supportedCustomerTypes && (
              <span className="error-msg">{errors.supportedCustomerTypes.message}</span>
            )}
          </FormItem>
        </FormWrapper>
      </Container>
    </Modal>
  );
};

export default MarketplaceGeneralSettingsForm;
